import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '@/lib/apiClient';

// ── Types ─────────────────────────────────────────────────────────────

export interface GoogleIntegrationStatus {
  domain_id: string;
  gsc_connected: boolean;
  ga4_connected: boolean;
  gsc_property: string | null;
  ga4_property: string | null;
  last_sync: string | null;
  next_refresh_available: string | null;
  status: 'active' | 'expired' | 'revoked' | 'not_connected';
  data_quality: {
    overall_score: number;
    recommended_mode: 'ai_only' | 'hybrid' | 'data_driven';
    sufficient_for_bp: boolean;
  } | null;
}

export interface IntegrationState {
  byDomain: Record<string, GoogleIntegrationStatus>;
  statusByDomain: Record<string, 'idle' | 'loading' | 'succeeded' | 'failed'>;
  errorByDomain: Record<string, string | undefined>;
  mutatingByDomain: Record<string, boolean>;
}

const initialState: IntegrationState = {
  byDomain: {},
  statusByDomain: {},
  errorByDomain: {},
  mutatingByDomain: {},
};

// ── Async Thunks ──────────────────────────────────────────────────────

export const fetchGoogleStatus = createAsyncThunk(
  'integration/fetchGoogleStatus',
  async (domainId: string, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/external/google/status/${domainId}`);
      return { domain_id: domainId, ...res.data } as GoogleIntegrationStatus;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.detail || 'Failed to fetch Google status');
    }
  },
  {
    condition: (domainId, { getState }) => {
      const state = (getState() as { integration: IntegrationState }).integration;
      // Skip if already loading to prevent double-fetch from React Strict Mode
      if (state.statusByDomain[domainId] === 'loading') {
        return false;
      }
    },
  },
);

export const revokeGoogleIntegration = createAsyncThunk(
  'integration/revokeGoogleIntegration',
  async (domainId: string, { rejectWithValue }) => {
    try {
      await apiClient.post('/external/google/revoke', { domain_id: domainId });
      return domainId;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.detail || 'Failed to revoke Google integration');
    }
  },
);

export const configureGoogleProperties = createAsyncThunk(
  'integration/configureGoogleProperties',
  async (
    { domainId, gscProperty, ga4Property }: { domainId: string; gscProperty: string; ga4Property?: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await apiClient.post(`/v1/domains/${domainId}/google-configure`, {
        gsc_property: gscProperty,
        ga4_property: ga4Property || null,
      });
      return { domainId, ...res.data };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.detail || 'Failed to configure Google properties');
    }
  },
);

export const triggerDataIngestion = createAsyncThunk(
  'integration/triggerDataIngestion',
  async (domainId: string, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/v1/domains/${domainId}/data-ingest`);
      return { domainId, ...res.data };
    } catch (err: any) {
      // 429 = cooldown active
      const status = err?.response?.status;
      const daysRemaining = err?.response?.headers?.['x-days-remaining'];
      if (status === 429 && daysRemaining) {
        return rejectWithValue(`Data refresh on cooldown. Available in ${daysRemaining} day(s).`);
      }
      return rejectWithValue(err?.response?.data?.detail || 'Failed to start data ingestion');
    }
  },
);

// ── Slice ─────────────────────────────────────────────────────────────

const integrationSlice = createSlice({
  name: 'integration',
  initialState,
  reducers: {
    clearIntegrationError(state, action) {
      const domainId = action.payload as string;
      state.errorByDomain[domainId] = undefined;
    },
    resetIntegrationStatus(state, action) {
      const domainId = action.payload as string;
      state.statusByDomain[domainId] = 'idle';
    },
  },
  extraReducers: (builder) => {
    // fetchGoogleStatus
    builder
      .addCase(fetchGoogleStatus.pending, (state, action) => {
        state.statusByDomain[action.meta.arg] = 'loading';
        state.errorByDomain[action.meta.arg] = undefined;
      })
      .addCase(fetchGoogleStatus.fulfilled, (state, action) => {
        const data = action.payload;
        state.byDomain[data.domain_id] = data;
        state.statusByDomain[data.domain_id] = 'succeeded';
      })
      .addCase(fetchGoogleStatus.rejected, (state, action) => {
        state.statusByDomain[action.meta.arg] = 'failed';
        state.errorByDomain[action.meta.arg] = action.payload as string;
      });

    // revokeGoogleIntegration
    builder
      .addCase(revokeGoogleIntegration.pending, (state, action) => {
        state.mutatingByDomain[action.meta.arg] = true;
        state.errorByDomain[action.meta.arg] = undefined;
      })
      .addCase(revokeGoogleIntegration.fulfilled, (state, action) => {
        const domainId = action.payload;
        state.byDomain[domainId] = {
          domain_id: domainId,
          gsc_connected: false,
          ga4_connected: false,
          gsc_property: null,
          ga4_property: null,
          last_sync: null,
          next_refresh_available: null,
          status: 'revoked',
          data_quality: null,
        };
        state.statusByDomain[domainId] = 'succeeded';
        state.mutatingByDomain[domainId] = false;
      })
      .addCase(revokeGoogleIntegration.rejected, (state, action) => {
        state.mutatingByDomain[action.meta.arg] = false;
        state.errorByDomain[action.meta.arg] = action.payload as string;
      });

    // configureGoogleProperties
    builder
      .addCase(configureGoogleProperties.pending, (state, action) => {
        state.mutatingByDomain[action.meta.arg.domainId] = true;
        state.errorByDomain[action.meta.arg.domainId] = undefined;
      })
      .addCase(configureGoogleProperties.fulfilled, (state, action) => {
        const domainId = action.meta.arg.domainId;
        state.mutatingByDomain[domainId] = false;
        // Update stored integration with selected properties
        if (state.byDomain[domainId]) {
          state.byDomain[domainId].gsc_property = action.payload.gsc_property;
          state.byDomain[domainId].ga4_property = action.payload.ga4_property;
          state.byDomain[domainId].gsc_connected = true;
          state.byDomain[domainId].ga4_connected = !!action.payload.ga4_property;
        }
      })
      .addCase(configureGoogleProperties.rejected, (state, action) => {
        state.mutatingByDomain[action.meta.arg.domainId] = false;
        state.errorByDomain[action.meta.arg.domainId] = action.payload as string;
      });

    // triggerDataIngestion
    builder
      .addCase(triggerDataIngestion.pending, (state, action) => {
        state.mutatingByDomain[action.meta.arg] = true;
        state.errorByDomain[action.meta.arg] = undefined;
      })
      .addCase(triggerDataIngestion.fulfilled, (state, action) => {
        state.mutatingByDomain[action.meta.arg] = false;
      })
      .addCase(triggerDataIngestion.rejected, (state, action) => {
        state.mutatingByDomain[action.meta.arg] = false;
        state.errorByDomain[action.meta.arg] = action.payload as string;
      });
  },
});

export const { clearIntegrationError, resetIntegrationStatus } = integrationSlice.actions;
export default integrationSlice.reducer;
